import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { CentroContexto, ContextoResposta, EmpresaContexto } from '@/lib/tipos';
import { useAtualizarParams, useParamUrl } from './useUrlEstado';

const CHAVE_CENTRO = 'wms.centro';
const CHAVE_EMPRESA = 'wms.empresa';

interface ValorContexto {
  /** Todos os centros liberados, de todas as empresas do usuário. */
  centros: CentroContexto[];
  /** Só os da empresa selecionada — o que o seletor de centro mostra. */
  centrosDaEmpresa: CentroContexto[];
  centro: number | null;
  centroAtual: CentroContexto | null;
  empresas: EmpresaContexto[];
  empresa: number | null;
  empresaAtual: EmpresaContexto | null;
  definirCentro: (centro: number) => void;
  definirEmpresa: (empresa: number) => void;
  /**
   * A URL pediu um centro ou uma empresa que este usuário não alcança.
   *
   * `null` no caso normal. Quando vem preenchido, a aplicação para e diz
   * isso — ver `AcessoNegado` no AppShell.
   */
  acessoNegado: { centro: number | null; empresa: number | null } | null;
  carregando: boolean;
  erro: unknown;
  recarregar: () => void;
}

const Contexto = React.createContext<ValorContexto | null>(null);

function lerLocal(): number | null {
  try {
    const bruto = localStorage.getItem(CHAVE_CENTRO);
    const n = bruto === null ? Number.NaN : Number(bruto);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function gravarLocal(centro: number): void {
  try {
    localStorage.setItem(CHAVE_CENTRO, String(centro));
  } catch {
    // Navegador com storage bloqueado: o centro ainda vive na URL.
  }
}

function lerLocalEmpresa(): number | null {
  try {
    const bruto = localStorage.getItem(CHAVE_EMPRESA);
    const n = bruto === null ? Number.NaN : Number(bruto);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function gravarLocalEmpresa(empresa: number): void {
  try {
    localStorage.setItem(CHAVE_EMPRESA, String(empresa));
  } catch {
    // Idem: a empresa ainda vive na URL.
  }
}

export function ProvedorContexto({ children }: { children: React.ReactNode }): JSX.Element {
  const consulta = useQuery<ContextoResposta>({
    queryKey: ['contexto'],
    queryFn: () => api.get<ContextoResposta>('/api/contexto'),
    staleTime: 30 * 60_000,
  });

  const centros = React.useMemo(() => consulta.data?.centros ?? [], [consulta.data]);
  const [centroUrl, definirCentroUrl] = useParamUrl('centro');
  const [empresaUrl] = useParamUrl('empresa');
  const atualizarParams = useAtualizarParams();

  /**
   * As empresas que este usuário enxerga.
   *
   * Sai dos CENTROS que a API liberou — nunca de uma lista solta de empresas.
   * Assim a regra fica com um dono só: quem decide o que aparece é o escopo do
   * servidor, e a tela não tem como oferecer uma empresa cujo centro ela não
   * poderia abrir. O nome vem de `/api/contexto`; sem nome, fica o número.
   */
  const empresas = React.useMemo<EmpresaContexto[]>(() => {
    const nomes = new Map(
      (consulta.data?.empresas ?? []).map((e) => [e.empresa, e.nome?.trim() || null]),
    );
    const contagem = new Map<number, number>();
    for (const c of centros) contagem.set(c.empresa, (contagem.get(c.empresa) ?? 0) + 1);
    return [...contagem.entries()]
      .map(([empresa, quantos]) => ({
        empresa,
        nome: nomes.get(empresa) ?? null,
        centros: quantos,
      }))
      .sort((a, b) => a.empresa - b.empresa);
  }, [centros, consulta.data]);

  /**
   * Precedência: centro na URL > empresa na URL > o que ficou no navegador.
   *
   * O centro da URL ganha de tudo e ARRASTA a empresa junto, porque link
   * compartilhado tem de abrir exatamente no centro de quem mandou — abrir
   * naquele centro com a empresa de outra pessoa selecionada seria estado
   * incoerente, do tipo que faz a tela contar um lugar e listar outro.
   */
  const empresa = React.useMemo(() => {
    const existe = (n: number | null): n is number =>
      n !== null && Number.isFinite(n) && empresas.some((e) => e.empresa === n);

    const centroDaUrl = centroUrl === '' ? null : Number(centroUrl);
    const doCentroDaUrl = centros.find((c) => c.centroEstoque === centroDaUrl)?.empresa ?? null;
    if (existe(doCentroDaUrl)) return doCentroDaUrl;

    const daUrl = empresaUrl === '' ? null : Number(empresaUrl);
    if (existe(daUrl)) return daUrl;

    const doLocal = lerLocalEmpresa();
    if (existe(doLocal)) return doLocal;

    const doCentroLocal = centros.find((c) => c.centroEstoque === lerLocal())?.empresa ?? null;
    if (existe(doCentroLocal)) return doCentroLocal;

    const doPadrao =
      centros.find((c) => c.centroEstoque === consulta.data?.centroPadrao)?.empresa ?? null;
    return existe(doPadrao) ? doPadrao : (empresas[0]?.empresa ?? null);
  }, [centroUrl, empresaUrl, centros, empresas, consulta.data]);

  /** Só os centros da empresa selecionada — é isso que o seletor de centro vê. */
  const centrosDaEmpresa = React.useMemo(
    () => (empresa === null ? centros : centros.filter((c) => c.empresa === empresa)),
    [centros, empresa],
  );

  const centro = React.useMemo(() => {
    // O centro tem de pertencer à empresa selecionada; se não pertence, ele não
    // é "o centro escolhido" — é resíduo de outra escolha.
    const valido = (n: number | null): n is number =>
      n !== null && Number.isFinite(n) && centrosDaEmpresa.some((c) => c.centroEstoque === n);

    const daUrl = centroUrl === '' ? null : Number(centroUrl);
    if (valido(daUrl)) return daUrl;
    const doLocal = lerLocal();
    if (valido(doLocal)) return doLocal;
    const padrao = consulta.data?.centroPadrao ?? null;
    if (valido(padrao)) return padrao;
    return centrosDaEmpresa[0]?.centroEstoque ?? null;
  }, [centroUrl, centrosDaEmpresa, consulta.data]);

  /*
   * URL PEDINDO O QUE NÃO É SEU — negar em voz alta, não corrigir calado.
   *
   * O servidor já recusa: as 73 rotas que leem dado de estoque passam por
   * `exigirCentro`/`exigirEmpresa`, e `/api/contexto` só devolve os centros
   * liberados. Trocar o número na barra nunca leu dado alheio, e continua
   * não lendo.
   *
   * O que a tela fazia era pior de outro jeito: como o centro da URL não
   * passava na validação, ela caía no primeiro centro permitido e reescrevia
   * a barra. Quem abriu `?centro=3201` via, sem aviso nenhum, os números do
   * 2201 — e o caso comum disso não é má fé, é um link colado de um colega
   * que tem outro acesso. Mostrar o galpão errado calado é como uma
   * conferência sai conferindo a coisa errada.
   *
   * Só acusa depois que a lista chegou (`isSuccess`): antes disso todo centro
   * parece proibido, porque ainda não se sabe quais são os permitidos.
   */
  const acessoNegado = React.useMemo(() => {
    if (!consulta.isSuccess) return null;

    const pedidoCentro = centroUrl === '' ? null : Number(centroUrl);
    const pedidoEmpresa = empresaUrl === '' ? null : Number(empresaUrl);

    const centroProibido =
      pedidoCentro !== null &&
      Number.isFinite(pedidoCentro) &&
      !centros.some((c) => c.centroEstoque === pedidoCentro);

    const empresaProibida =
      pedidoEmpresa !== null &&
      Number.isFinite(pedidoEmpresa) &&
      !empresas.some((e) => e.empresa === pedidoEmpresa);

    if (!centroProibido && !empresaProibida) return null;
    return {
      centro: centroProibido ? pedidoCentro : null,
      empresa: empresaProibida ? pedidoEmpresa : null,
    };
  }, [consulta.isSuccess, centroUrl, empresaUrl, centros, empresas]);

  /*
   * Mantém a URL sincronizada sem empilhar histórico a cada navegação.
   *
   * `acessoNegado` trava esta sincronização de propósito: reescrever a barra
   * apagaria justamente o número que a pessoa precisa ver na mensagem, e o
   * pedido recusado sumiria do histórico como se nunca tivesse existido.
   */
  React.useEffect(() => {
    if (acessoNegado) return;
    if (centro !== null && centroUrl !== String(centro)) {
      definirCentroUrl(String(centro), { substituir: true });
    }
  }, [acessoNegado, centro, centroUrl, definirCentroUrl]);

  const definirCentro = React.useCallback(
    (novo: number) => {
      gravarLocal(novo);
      const empresaDoCentro = centros.find((c) => c.centroEstoque === novo)?.empresa ?? null;
      if (empresaDoCentro !== null) gravarLocalEmpresa(empresaDoCentro);
      // Numa escrita só: dois `definir` seguidos se sobrepõem, porque o segundo
      // lê a URL antes de o primeiro aplicar.
      atualizarParams({
        centro: String(novo),
        empresa: empresaDoCentro === null ? null : String(empresaDoCentro),
      });
    },
    [centros, atualizarParams],
  );

  /**
   * Trocar de empresa leva o centro junto.
   *
   * Empresa sem centro não é estado útil: toda consulta do sistema pede um
   * centro. Escolher a empresa já posiciona no primeiro centro dela — um
   * clique resolve a troca inteira, em vez de exigir um segundo no centro.
   * Se o centro que estava no navegador pertence à empresa nova, ele é
   * mantido: voltar para uma empresa não deve perder onde se estava nela.
   */
  const definirEmpresa = React.useCallback(
    (nova: number) => {
      const daEmpresa = centros.filter((c) => c.empresa === nova);
      const preferido = daEmpresa.find((c) => c.centroEstoque === lerLocal());
      const alvo = preferido ?? daEmpresa[0];
      gravarLocalEmpresa(nova);
      if (alvo) gravarLocal(alvo.centroEstoque);
      atualizarParams({
        empresa: String(nova),
        centro: alvo ? String(alvo.centroEstoque) : null,
      });
    },
    [centros, atualizarParams],
  );

  const centroAtual = centros.find((c) => c.centroEstoque === centro) ?? null;
  const empresaAtual = empresas.find((e) => e.empresa === empresa) ?? null;

  const valor = React.useMemo<ValorContexto>(
    () => ({
      centros,
      centrosDaEmpresa,
      centro,
      centroAtual,
      empresas,
      empresa,
      empresaAtual,
      definirCentro,
      definirEmpresa,
      acessoNegado,
      carregando: consulta.isLoading,
      erro: consulta.error,
      recarregar: () => void consulta.refetch(),
    }),
    [
      centros,
      centrosDaEmpresa,
      centro,
      centroAtual,
      empresas,
      empresa,
      empresaAtual,
      definirCentro,
      definirEmpresa,
      acessoNegado,
      consulta,
    ],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useContexto(): ValorContexto {
  const valor = React.useContext(Contexto);
  if (!valor) throw new Error('useContexto precisa estar dentro de <ProvedorContexto>.');
  return valor;
}

/** Centro obrigatório para as consultas de dados; `null` desabilita a query. */
export function useCentro(): number | null {
  return useContexto().centro;
}
